import type { ReactNode } from 'react';
import { T, Var } from 'gt-react';
import { PencilSimpleIcon, PlusIcon } from '@phosphor-icons/react';
import { Button } from 'src/components/ui/button';
import {
  SelectDropdown,
  type SelectDropdownOption,
} from 'src/components/common/SelectDropdown';
import type { CoreEnumDTO } from 'src/api/core/core-enums/useCoreEnums';
import type { CoreEnumResponse } from 'src/api/core/core-enums/useCreateCoreEnum';
import { CoreEnumModal } from 'src/components/core/CoreEnumModal';
import { CORE_ENUM_NONE_VALUE } from './coreEnumPickerSelection';

export function selectCoreEnumId(
  option: SelectDropdownOption | null,
): string | null {
  if (!option || option.id === CORE_ENUM_NONE_VALUE) return null;
  return option.id;
}

export type CoreEnumPickerViewModel = {
  className: string;
  options: SelectDropdownOption[];
  selected: SelectDropdownOption | null;
  selectedEnum: CoreEnumDTO | undefined;
  selectedPreview: string | null;
  helpText?: ReactNode;
  disabled: boolean;
  isLoading: boolean;
  placeholder: string;
  searchPlaceholder: string;
  createOpen: boolean;
  editTarget: CoreEnumDTO | null;
  createDescription?: ReactNode;
  optionNamePlaceholder?: string;
  onChange: (enumId: string | null) => void;
  onCreate: () => void;
  onEdit: (coreEnum: CoreEnumDTO) => void;
  onCloseModal: () => void;
  onSaved: (saved: CoreEnumResponse) => void;
};

function PickerHelp({ helpText }: { helpText?: ReactNode }) {
  if (!helpText) return null;
  return (
    <div className="space-y-1 text-[11px] leading-snug text-muted-foreground">
      {helpText}
    </div>
  );
}

function PickerPreview({
  selectedPreview,
}: {
  selectedPreview: string | null;
}) {
  if (selectedPreview == null) return null;
  return (
    <p className="text-[11px] text-muted-foreground">
      <T context="preview of selected core enum option names">
        Values: <Var>{selectedPreview}</Var>
      </T>
    </p>
  );
}

function EditSelectedEnumButton({
  selectedEnum,
  disabled,
  onEdit,
}: {
  selectedEnum: CoreEnumDTO | undefined;
  disabled: boolean;
  onEdit: (coreEnum: CoreEnumDTO) => void;
}) {
  if (!selectedEnum) return null;
  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      disabled={disabled}
      onClick={() => onEdit(selectedEnum)}
    >
      <PencilSimpleIcon className="size-3.5" />
      <T context="button to edit the selected core enum from a picker">
        Edit options
      </T>
    </Button>
  );
}

function PickerActions({
  model,
}: {
  model: Pick<
    CoreEnumPickerViewModel,
    'disabled' | 'selectedEnum' | 'onCreate' | 'onEdit'
  >;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={model.disabled}
        onClick={model.onCreate}
      >
        <PlusIcon className="size-3.5" />
        <T context="button to create a core enum from a picker">Create enum</T>
      </Button>
      <EditSelectedEnumButton
        selectedEnum={model.selectedEnum}
        disabled={model.disabled}
        onEdit={model.onEdit}
      />
    </div>
  );
}

export function CoreEnumPickerView(model: CoreEnumPickerViewModel) {
  return (
    <div className={model.className}>
      <SelectDropdown
        options={model.options}
        value={model.selected}
        onChange={(option) => model.onChange(selectCoreEnumId(option))}
        placeholder={model.placeholder}
        searchPlaceholder={model.searchPlaceholder}
        disabled={model.disabled || model.isLoading}
        size="sm"
        fullWidth
      />
      <PickerHelp helpText={model.helpText} />
      <PickerPreview selectedPreview={model.selectedPreview} />
      <PickerActions model={model} />
      <CoreEnumModal
        open={model.createOpen || model.editTarget !== null}
        onClose={model.onCloseModal}
        enumToEdit={model.editTarget}
        createDescription={model.createDescription}
        optionNamePlaceholder={model.optionNamePlaceholder}
        onSaved={model.onSaved}
      />
    </div>
  );
}
